/* China Festival：独立的灯笼、弹窗和商品 Tabs 交互 */
(() => {
  if (customElements.get('china-festival')) return;

  customElements.define(
    'china-festival',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;

        this.controller = new AbortController();

        const on = (node, event, callback) => {
          node.addEventListener(event, callback, {
            signal: this.controller.signal
          });
        };

        this.tabs = [...this.querySelectorAll('[data-tab]')];
        this.panels = [...this.querySelectorAll('[data-panel]')];
        this.dialog = this.querySelector('dialog');

        /* 从对应灯笼区块读取后台配置 */
        this.lanternData = Object.fromEntries(
          [...this.querySelectorAll('[data-offer]')].map((element) => [
            element.dataset.offer,
            { ...element.dataset }
          ])
        );

        /* 产品 Tabs */
        this.querySelector('[data-tabs]').setAttribute('role', 'tablist');

        this.tabs.forEach((tab) => {
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', tab.hash.slice(1));

          on(tab, 'click', (event) => {
            event.preventDefault();
            this.activate(tab.dataset.tab);
          });

          on(tab, 'keydown', (event) => {
            const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

            if (!keys.includes(event.key)) return;

            event.preventDefault();

            const currentIndex = this.tabs.indexOf(tab);
            let nextIndex;

            if (event.key === 'Home') {
              nextIndex = 0;
            } else if (event.key === 'End') {
              nextIndex = this.tabs.length - 1;
            } else {
              const direction = event.key === 'ArrowRight' ? 1 : -1;

              nextIndex =
                (currentIndex + direction + this.tabs.length) %
                this.tabs.length;
            }

            this.activate(this.tabs[nextIndex].dataset.tab);
            this.tabs[nextIndex].focus();
          });
        });

        this.panels.forEach((panel) => {
          panel.setAttribute('role', 'tabpanel');
          panel.tabIndex = 0;
        });

        this.classList.add('cf-ready');
        this.activate(this.tabs[0]?.dataset.tab);

        /* 展开最后四件商品 */
        this.querySelectorAll('[data-expand]').forEach((button) => {
          button.hidden = button
            .closest('[data-panel]')
            .classList.contains('cf-expanded');

          on(button, 'click', () => {
            const panel = button.closest('[data-panel]');

            panel.classList.add('cf-expanded');
            button.setAttribute('aria-expanded', 'true');

            panel.querySelector('.cf-extra a')?.focus({
              preventScroll: true
            });

            button.hidden = true;
          });
        });

        /* 点击灯笼，播放动画并打开弹窗 */
        this.querySelectorAll('[data-lantern]').forEach((button) => {
          button.disabled = false;

          on(button, 'click', () => {
            clearTimeout(this.openTimer);

            this.querySelectorAll('.cf-opening').forEach((element) => {
              element.classList.remove('cf-opening');
            });

            this.activeOffer = button.dataset.lantern;
            this.opener = button;

            button.classList.add('cf-opening');

            this.openTimer = setTimeout(() => {
              button.classList.remove('cf-opening');

              if (!this.isConnected) return;

              const data = this.lanternData[this.activeOffer];

              this.querySelector('[data-modal-title]').textContent =
                data.title;

              this.querySelector('[data-modal-symbol]').textContent =
                data.symbol;

              this.querySelector('[data-modal-discount]').textContent =
                data.discount;

              this.querySelector('[data-modal-code]').textContent =
                data.code;

              this.querySelector('[data-redeem]').textContent =
                data.cta;

              this.querySelector('[data-copy-error]').textContent = '';

              this.manualOffer = null;
              this.dialog.showModal();
            }, this.reducedMotion() ? 0 : 460);
          });
        });

        /* 关闭弹窗 */
        on(this.querySelector('[data-close]'), 'click', () => {
          this.dialog.close();
        });

        /* 点击弹窗外的遮罩关闭 */
        on(this.dialog, 'click', (event) => {
          const rect = this.dialog.getBoundingClientRect();

          const outside =
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom;

          if (event.target === this.dialog && outside) {
            this.dialog.close();
          }
        });

        /* 复制优惠码，然后跳转到对应商品 Tab */
        on(this.querySelector('[data-redeem]'), 'click', async () => {
          const data = this.lanternData[this.activeOffer];
          const button = this.querySelector('[data-redeem]');

          button.disabled = true;

          const copied = await this.copy(data.code);

          button.disabled = false;

          if (!this.isConnected || !this.dialog.open) return;

          if (!copied) {
            this.querySelector('[data-copy-error]').textContent =
              `${this.dataset.failed} ${data.code}`;

            /*
             * 复制失败时保留弹窗。
             * 用户手动复制后，再次点击按钮继续。
             */
            if (this.manualOffer !== this.activeOffer) {
              this.manualOffer = this.activeOffer;
              return;
            }
          }

          this.manualOffer = null;
          this.dialog.close();

          this.activate(this.activeOffer);
          this.scrollToProducts();

          this.tabs
            .find((tab) => tab.dataset.tab === this.activeOffer)
            ?.focus({ preventScroll: true });

          const status = this.querySelector('[data-status]');

          status.textContent =
            `${copied ? this.dataset.copied : this.dataset.failed} ${data.code}`;

          status.classList.add('cf-visible');

          clearTimeout(this.toastTimer);

          this.toastTimer = setTimeout(() => {
            status.classList.remove('cf-visible');
          }, 5000);
        });

        /* Hero 和底部购物按钮 */
        this.querySelectorAll('[data-shop]').forEach((link) => {
          link.href = `#${this.querySelector('.cf-products').id}`;

          on(link, 'click', (event) => {
            event.preventDefault();
            this.scrollToProducts();

            this.tabs
              .find((tab) => tab.getAttribute('aria-selected') === 'true')
              ?.focus({ preventScroll: true });
          });
        });

        /* Shopify 编辑器中选中区块时，显示对应 Tab */
        on(document, 'shopify:block:select', (event) => {
          const id = event.detail?.blockId;

          if (this.lanternData[id]) {
            this.activate(id);
          }
        });
      }

      /* 激活指定 Tab */
      activate(id) {
        this.tabs.forEach((tab) => {
          const active = tab.dataset.tab === id;

          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== id;
        });
      }

      /* 尊重系统减少动态效果设置 */
      reducedMotion() {
        return matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /* 滚动到商品区域 */
      scrollToProducts() {
        this.querySelector('.cf-products').scrollIntoView({
          behavior: this.reducedMotion() ? 'instant' : 'smooth',
          block: 'start'
        });
      }

      /* 复制优惠码，包含兼容处理 */
      async copy(code) {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(code);
            return true;
          }
        } catch (_) {
          /* 继续尝试兼容方式 */
        }

        const field = document.createElement('textarea');

        field.value = code;
        field.className = 'cf-copy-field';
        field.setAttribute('aria-label', 'Coupon code');

        this.dialog.append(field);
        field.focus();
        field.select();

        let success = false;

        try {
          success = document.execCommand('copy');
        } catch (_) {
          /* 返回失败，由弹窗提示用户手动复制 */
        }

        field.remove();
        this.querySelector('[data-redeem]').focus();

        return success;
      }

      /* Section 被移除时清理事件和计时器 */
      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;

        clearTimeout(this.openTimer);
        clearTimeout(this.toastTimer);

        if (this.dialog?.open) {
          this.dialog.close();
        }
      }
    }
  );
})();